'use strict';

Object.defineProperty(exports, "__esModule", {
	value: true
});

var _stringify = require('babel-runtime/core-js/json/stringify');

var _stringify2 = _interopRequireDefault(_stringify);

var _classCallCheck2 = require('babel-runtime/helpers/classCallCheck');

var _classCallCheck3 = _interopRequireDefault(_classCallCheck2);

var _throttle = require('lodash/throttle');

var _throttle2 = _interopRequireDefault(_throttle);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

/**
 * Implement an inactivity timeout that leverages [local storage](https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage) to detect activity in multiple browser windows and tabs
 * @class InactivityTimeout
 * @example
 * import InactivityTimeout from 'storis-inactivity-timeout';
 * 
 * // override some of the default options
 * const activityInterval = 1000;
 * const idlePeriod = 30000;
 * const warningPeriod = 10000;
 * 
 * const warningCallback = () => {
 *   // user has been idle for 30 seconds, start the warning period...
 * };
 * 
 * const timeoutCallback = () => {
 *   // user has been idle through the warning period of 10 seconds and is now timed out...
 * };
 * 
 * const resumeActivityCallback = () => {
 *   // user has resumed activity after being warned!
 * };
 * 
 * // instantiate with our options and callbacks
 * const inactivity = new InactivityTimeout({
 *   activityInterval,
 *   idlePeriod,
 *   warningPeriod,
 *   warningCallback,
 *   timeoutCallback,
 *   resumeActivityCallback,
 * });
 * 
 * // attach default set of event listeners to the window (default element)
 * inactivity.addEventListeners();
 * 
 * // start checking for activity
 * inactivity.startActivityInterval();
 */
var InactivityTimeout =
/**
 * @property {Object} [element = window] [EventTarget](https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener) used for activity detection
 * @property {number} [activityInterval = 500] Milliseconds between activity checks
 * @property {number} [idlePeriod = 300000] Milliseconds of inactivity prior to warning
 * @property {number} [warningPeriod = 30000] Milliseconds of inactivity prior to timeout
 * @property {string} [activityStorageKey = 'last-activity'] Local storage key for latest activity
 * @property {function} warningCallback Warning callback, invoked after idlePeriod
 * @property {function} timeoutCallback Timeout callback, invoked after idlePeriod and warningPeriod
 * @property {function} resumeActivityCallback Resumed callback, invoked only if warning has occurred
 */
function InactivityTimeout() {
	var _this = this;

	var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
	(0, _classCallCheck3.default)(this, InactivityTimeout);

	this.addEventListeners = function () {
		var events = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : ['mousemove', 'mousedown', 'keypress', 'wheel', 'touchmove', 'pointermove', 'mousewheel', 'DOMMouseScroll', 'MSPointerMove'];

		// attach event listeners
		events.forEach(function (event) {
			_this.element.addEventListener(event, (0, _throttle2.default)(_this.onActivity, 1000), false);
		});
	};

	this.checkLastActivity = function () {
		var lastActivity = localStorage.getItem(_this.storageKey) || 0;
		var idleTime = Date.now() - lastActivity;
		if (idleTime > _this.idlePeriod) {
			if (_this.timedOut) {
				// user has been inactive since timeout
			} else if (idleTime > _this.idlePeriod + _this.warningPeriod) {
				// user has timed out
				_this.timedOut = true;
				_this.timeoutCallback();
			} else if (!_this.warned) {
				// user has been idle too long, warn them
				_this.warned = true;
				_this.warningCallback();
			}
		} else if (_this.warned) {
			// user has resumed activity
			_this.timedOut = false;
			_this.warned = false;
			_this.resumeActivityCallback();
		}
	};

	this.onActivity = function () {
		localStorage.setItem(_this.storageKey, (0, _stringify2.default)(Date.now()));
	};

	this.startActivityInterval = function () {
		// log the first activity
		_this.onActivity();
		window.setInterval(_this.checkLastActivity, _this.activityInterval);
	};

	// destructure with defaults
	var _options$element = options.element,
	    element = _options$element === undefined ? window : _options$element,
	    _options$activityInte = options.activityInterval,
	    activityInterval = _options$activityInte === undefined ? 500 : _options$activityInte,
	    _options$idlePeriod = options.idlePeriod,
	    idlePeriod = _options$idlePeriod === undefined ? 5 * 60 * 1000 : _options$idlePeriod,
	    _options$warningPerio = options.warningPeriod,
	    warningPeriod = _options$warningPerio === undefined ? 30 * 1000 : _options$warningPerio,
	    _options$timeoutCallb = options.timeoutCallback,
	    timeoutCallback = _options$timeoutCallb === undefined ? function () {} : _options$timeoutCallb,
	    _options$warningCallb = options.warningCallback,
	    warningCallback = _options$warningCallb === undefined ? function () {} : _options$warningCallb,
	    _options$resumeActivi = options.resumeActivityCallback,
	    resumeActivityCallback = _options$resumeActivi === undefined ? function () {} : _options$resumeActivi,
	    _options$activityStor = options.activityStorageKey,
	    activityStorageKey = _options$activityStor === undefined ? 'last-activity' : _options$activityStor;

	// apply to this instance

	this.element = element;
	this.activityInterval = activityInterval;
	this.idlePeriod = idlePeriod;
	this.warningPeriod = warningPeriod;
	this.timeoutCallback = timeoutCallback;
	this.warningCallback = warningCallback;
	this.resumeActivityCallback = resumeActivityCallback;
	this.activityStorageKey = activityStorageKey;
	this.warned = false;
	this.timedOut = false;
	this.storageKey = activityStorageKey;
}

/**
 * Add event listeners to element  
 * @function
 * @param {Array<string>} events - Events to interpret as activity, if unspecified the following events will be used for activity detection:  
 * - [mousemove](https://developer.mozilla.org/en-US/docs/Web/Events/mousemove)
 * - [mousedown](https://developer.mozilla.org/en-US/docs/Web/Events/mousedown)
 * - [keypress](https://developer.mozilla.org/en-US/docs/Web/Events/keypress)
 * - [wheel](https://developer.mozilla.org/en-US/docs/Web/Events/wheel)
 * - [touchmove](https://developer.mozilla.org/en-US/docs/Web/Events/touchmove)
 * - [pointermove](https://developer.mozilla.org/en-US/docs/Web/Events/pointermove)
 * - [mousewheel](https://developer.mozilla.org/en-US/docs/Web/Events/mousewheel)
 * - [DOMMouseScroll](https://developer.mozilla.org/en-US/docs/Web/Events/DOMMouseScroll)
 * - [MSPointerMove](https://msdn.microsoft.com/en-us/library/hh771911(v=vs.85).aspx)
 */


/**
 * Check latest activity logged in local storage
 * @function
 * @private
 */


/**
 * Store a timestamp in local storage  
 * This function serves as the callback to activity events
 * @function
 * @private
 */


/**
 * Initialize by logging initial activity and starting the activity check interval
 * @function
 */
;

exports.default = InactivityTimeout;